/**
 * /logo — asset showcase page for screenshots and screen-recording GIFs.
 * All variants use the BellRinging phosphor icon on brand colors.
 * Not linked from Nav; direct-URL only.
 */
import { BellRinging } from "@phosphor-icons/react";

// Shared animation styles: gentle ring wiggle every ~2.5s, perfect for looping GIF
const ringKeyframes = `
@keyframes buzzerai-ring {
  0%, 60%, 100% { transform: rotate(0deg); }
  65% { transform: rotate(14deg); }
  70% { transform: rotate(-12deg); }
  75% { transform: rotate(10deg); }
  80% { transform: rotate(-8deg); }
  85% { transform: rotate(6deg); }
  90% { transform: rotate(-4deg); }
  95% { transform: rotate(2deg); }
}
.ring-animate { animation: buzzerai-ring 2.6s ease-in-out infinite; transform-origin: 50% 30%; }

@keyframes buzzerai-bee-hover {
  0%, 100% { transform: translateY(0) rotate(-2deg); }
  50%      { transform: translateY(-6px) rotate(2deg); }
}
.bee-hover { animation: buzzerai-bee-hover 1.6s ease-in-out infinite; transform-origin: 50% 50%; }

@keyframes buzzerai-bee-fly {
  0%   { transform: translateX(-10px); opacity: 0.6; }
  40%  { transform: translateX(0) rotate(-4deg); opacity: 1; }
  70%  { transform: translateX(4px) rotate(2deg); opacity: 1; }
  100% { transform: translateX(-10px); opacity: 0.6; }
}
.bee-fly { animation: buzzerai-bee-fly 2.2s ease-in-out infinite; transform-origin: 50% 50%; }

@keyframes buzzerai-bee-buzz {
  0%, 100% { transform: translateY(0) rotate(0deg); }
  25%      { transform: translateY(-4px) rotate(-3deg); }
  50%      { transform: translateY(-2px) rotate(2deg); }
  75%      { transform: translateY(-5px) rotate(-1deg); }
}
.bee-buzz { animation: buzzerai-bee-buzz 1.8s ease-in-out infinite; transform-origin: 50% 60%; }

@keyframes buzzerai-wing-flap {
  0%, 100% { transform: scaleY(1); }
  50%      { transform: scaleY(0.55); }
}
.wing-flap { animation: buzzerai-wing-flap 0.18s ease-in-out infinite; transform-origin: 50% 100%; }
`;

const Tile = ({ bg, label, children, testid }) => (
  <div className="paper-card overflow-hidden">
    <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
      <span className="overline">{label}</span>
      <span className="text-xs text-[#5C6B61]">{testid}</span>
    </div>
    <div
      className="flex items-center justify-center py-16"
      style={{ background: bg }}
      data-testid={testid}
    >
      {children}
    </div>
  </div>
);

export default function LogoShowcase() {
  return (
    <div className="min-h-screen bg-[#F7F6F2] py-16 px-6">
      <style>{ringKeyframes}</style>

      <div className="max-w-6xl mx-auto">
        <div className="mb-10">
          <div className="overline mb-2">Brand · Downloadables</div>
          <h1 className="text-3xl sm:text-4xl font-heading font-extrabold tracking-tight">BuzzerAI logo assets</h1>
          <p className="text-sm text-[#5C6B61] mt-2 max-w-xl">
            Screenshot any tile below, or record the animated ones as a GIF. All marks are rendered vector so any zoom stays sharp.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* Big mark on cream */}
          <Tile bg="#F7F6F2" label="Mark · light" testid="logo-mark-light">
            <div className="w-40 h-40 rounded-3xl bg-[#1E3A2F] flex items-center justify-center shadow-xl">
              <BellRinging size={110} color="#F7F6F2" weight="fill" />
            </div>
          </Tile>

          {/* Big mark on dark */}
          <Tile bg="#1E3A2F" label="Mark · dark" testid="logo-mark-dark">
            <div className="w-40 h-40 rounded-3xl bg-[#F7F6F2] flex items-center justify-center shadow-xl">
              <BellRinging size={110} color="#1E3A2F" weight="fill" />
            </div>
          </Tile>

          {/* Full wordmark on cream */}
          <Tile bg="#F7F6F2" label="Wordmark · light" testid="logo-word-light">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#1E3A2F] flex items-center justify-center">
                <BellRinging size={40} color="#F7F6F2" weight="fill" />
              </div>
              <span className="font-heading font-extrabold text-5xl tracking-tight text-[#121914]">BuzzerAI</span>
            </div>
          </Tile>

          {/* Full wordmark on dark */}
          <Tile bg="#1E3A2F" label="Wordmark · dark" testid="logo-word-dark">
            <div className="flex items-center gap-4">
              <div className="w-16 h-16 rounded-2xl bg-[#F7F6F2] flex items-center justify-center">
                <BellRinging size={40} color="#1E3A2F" weight="fill" />
              </div>
              <span className="font-heading font-extrabold text-5xl tracking-tight text-[#F7F6F2]">BuzzerAI</span>
            </div>
          </Tile>

          {/* Animated ring — light */}
          <Tile bg="#F7F6F2" label="Animated · light (GIF-ready)" testid="logo-anim-light">
            <div className="w-40 h-40 rounded-3xl bg-[#1E3A2F] flex items-center justify-center shadow-xl">
              <div className="ring-animate">
                <BellRinging size={110} color="#F7F6F2" weight="fill" />
              </div>
            </div>
          </Tile>

          {/* Animated ring — dark with accent */}
          <Tile bg="#1E3A2F" label="Animated · dark (GIF-ready)" testid="logo-anim-dark">
            <div className="w-40 h-40 rounded-3xl bg-[#F7F6F2] flex items-center justify-center shadow-xl">
              <div className="ring-animate">
                <BellRinging size={110} color="#D96C5B" weight="fill" />
              </div>
            </div>
          </Tile>

          {/* Wordmark + tagline lockup — social banner ratio */}
          <div className="md:col-span-2 paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Social banner · 1200 × 400 crop</span>
              <span className="text-xs text-[#5C6B61]">logo-banner</span>
            </div>
            <div
              className="flex items-center gap-8 px-14 py-20 bg-[#1E3A2F]"
              data-testid="logo-banner"
            >
              <div className="w-24 h-24 rounded-2xl bg-[#D96C5B] flex items-center justify-center shrink-0">
                <div className="ring-animate">
                  <BellRinging size={60} color="#F7F6F2" weight="fill" />
                </div>
              </div>
              <div>
                <div className="font-heading font-extrabold text-6xl tracking-tight text-[#F7F6F2] leading-none">BuzzerAI</div>
                <div className="mt-3 text-lg text-[#E1DFD5]">The warm welcome your guests never forget.</div>
              </div>
            </div>
          </div>

          {/* Favicon reference — the actual /favicon.svg */}
          <Tile bg="#E6E2D6" label="Favicon · what browsers show" testid="logo-favicon">
            <img
              src="/favicon.svg"
              alt="BuzzerAI favicon"
              className="w-24 h-24 rounded-2xl shadow-md"
            />
          </Tile>

          {/* Monochrome mark — for stamps, watermarks, single-color print */}
          <Tile bg="#F7F6F2" label="Monochrome · print / stamp" testid="logo-mono">
            <BellRinging size={140} color="#121914" weight="fill" />
          </Tile>
        </div>

        <div className="mt-12 paper-card p-6">
          <div className="overline mb-2">How to grab these</div>
          <ul className="text-sm text-[#5C6B61] space-y-2 list-disc pl-5">
            <li><b className="text-[#121914]">Screenshot</b>: Cmd/Ctrl + Shift + 4 on any tile, or right-click → "Save image as" on the favicon.</li>
            <li><b className="text-[#121914]">Record a GIF</b>: The two "Animated" tiles ring on a 2.6-second loop. Any screen-recording tool (Kap, ScreenToGif, macOS Screenshot → Record Selection) will capture a clean loop.</li>
            <li><b className="text-[#121914]">Need a vector file?</b> The favicon at <code className="bg-[#E6E2D6] px-1.5 py-0.5 rounded">/favicon.svg</code> is a full SVG you can drop into Figma, Canva, or Illustrator.</li>
          </ul>
        </div>

        {/* ---------- Concept exploration: bumblebee ---------- */}
        <div className="mt-16 mb-6">
          <div className="overline mb-2 text-[#D96C5B]">Concept exploration</div>
          <h2 className="text-2xl sm:text-3xl font-heading font-extrabold tracking-tight">Bumblebee direction — two options</h2>
          <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">
            Both are drawn on-brand (same green, coral, cream palette) so you're comparing shape and personality, not colors. Neither is live in the app yet — pick a favorite and I'll swap it everywhere.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          {/* --- OPTION A: Warm Bumble --- */}
          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option A · "Warm Bumble"</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-a</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#F7F6F2]" data-testid="logo-bee-a">
              <div className="w-40 h-40 rounded-3xl bg-[#1E3A2F] flex items-center justify-center shadow-xl">
                <BeeWarm size={110} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#E1DFD5] bg-[#F7F6F2]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A2F] flex items-center justify-center shrink-0">
                  <BeeWarm size={32} />
                </div>
                <span className="font-heading font-extrabold text-3xl tracking-tight text-[#121914]">BuzzerAI</span>
              </div>
              <p className="text-xs text-[#5C6B61] leading-relaxed">
                <b className="text-[#121914]">Plump, friendly, hospitality-first.</b> Reads as "warm welcome" — matches the current brand voice. Antennae give it character at small sizes (favicon still legible).
              </p>
            </div>
          </div>

          {/* --- OPTION B: Signal Bee --- */}
          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option B · "Signal Bee"</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-b</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#F7F6F2]" data-testid="logo-bee-b">
              <div className="w-40 h-40 rounded-3xl bg-[#1E3A2F] flex items-center justify-center shadow-xl">
                <BeeSignal size={110} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#E1DFD5] bg-[#F7F6F2]">
              <div className="flex items-center gap-3 mb-3">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A2F] flex items-center justify-center shrink-0">
                  <BeeSignal size={32} />
                </div>
                <span className="font-heading font-extrabold text-3xl tracking-tight text-[#121914]">BuzzerAI</span>
              </div>
              <p className="text-xs text-[#5C6B61] leading-relaxed">
                <b className="text-[#121914]">Geometric, in-motion, tech-forward.</b> Motion lines say "buzzing / notification / speed." Reads as AI-native instead of cutesy. Scales down to favicon cleanly as a bold silhouette.
              </p>
            </div>
          </div>

          {/* Animated variants side-by-side */}
          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option A · animated (GIF-ready)</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-a-anim</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#1E3A2F]" data-testid="logo-bee-a-anim">
              <div className="w-40 h-40 rounded-3xl bg-[#F7F6F2] flex items-center justify-center shadow-xl">
                <div className="bee-hover">
                  <BeeWarm size={110} />
                </div>
              </div>
            </div>
          </div>

          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option B · animated (GIF-ready)</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-b-anim</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#1E3A2F]" data-testid="logo-bee-b-anim">
              <div className="w-40 h-40 rounded-3xl bg-[#F7F6F2] flex items-center justify-center shadow-xl">
                <div className="bee-fly">
                  <BeeSignal size={110} />
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* ---------- Option C: the mix ---------- */}
        <div className="mt-10 mb-6">
          <div className="overline mb-2 text-[#D96C5B]">The mix · new</div>
          <h3 className="text-xl sm:text-2xl font-heading font-extrabold tracking-tight">Option C — "Hive Signal"</h3>
          <p className="text-sm text-[#5C6B61] mt-2 max-w-2xl">
            Round friendly body + honey stripes from Option A · coral buzz-lines and cleaner face from Option B. Meant to feel like a hospitable bee that also happens to be smart software.
          </p>
        </div>

        <div className="grid md:grid-cols-2 gap-6">
          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option C · mark</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-c</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#F7F6F2]" data-testid="logo-bee-c">
              <div className="w-40 h-40 rounded-3xl bg-[#1E3A2F] flex items-center justify-center shadow-xl">
                <BeeHybrid size={110} />
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#E1DFD5] bg-[#F7F6F2]">
              <div className="flex items-center gap-3">
                <div className="w-12 h-12 rounded-xl bg-[#1E3A2F] flex items-center justify-center shrink-0">
                  <BeeHybrid size={32} />
                </div>
                <span className="font-heading font-extrabold text-3xl tracking-tight text-[#121914]">BuzzerAI</span>
              </div>
            </div>
          </div>

          <div className="paper-card overflow-hidden">
            <div className="px-5 py-3 border-b border-[#E1DFD5] flex items-center justify-between">
              <span className="overline">Option C · animated (GIF-ready)</span>
              <span className="text-xs text-[#5C6B61]">logo-bee-c-anim</span>
            </div>
            <div className="flex items-center justify-center py-14 bg-[#1E3A2F]" data-testid="logo-bee-c-anim">
              <div className="w-40 h-40 rounded-3xl bg-[#F7F6F2] flex items-center justify-center shadow-xl">
                <div className="bee-buzz">
                  <BeeHybrid size={110} />
                </div>
              </div>
            </div>
            <div className="px-5 py-4 border-t border-[#E1DFD5] bg-[#F7F6F2]">
              <p className="text-xs text-[#5C6B61] leading-relaxed">
                Wings shimmer while the body gently hovers — animation is 1.8s per loop, perfect for a short GIF or looping Reel end-card.
              </p>
            </div>
          </div>
        </div>

        <div className="mt-8 paper-card p-6 bg-[#1E3A2F] text-[#F7F6F2]">
          <div className="overline mb-2 text-[#D96C5B]">Brand asset card · for other AIs</div>
          <p className="text-sm text-[#E1DFD5] leading-relaxed mb-4">
            One document you can paste into ChatGPT, Claude, Gemini, Midjourney, or any social scheduler to keep everything they generate on-brand. Includes colors, voice, tagline options, ready-to-use prompt snippets, and the logo SVG source.
          </p>
          <div className="flex flex-wrap gap-3">
            <a
              href="/brand-kit.md"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full bg-[#D96C5B] text-white px-4 py-2 text-sm font-semibold hover:bg-[#c25b4c]"
              data-testid="brand-kit-open"
            >
              Open brand-kit.md →
            </a>
            <a
              href="/brand-kit.md"
              download="buzzerai-brand-kit.md"
              className="inline-flex items-center gap-2 rounded-full bg-[#F7F6F2] text-[#1E3A2F] px-4 py-2 text-sm font-semibold hover:bg-white"
              data-testid="brand-kit-download"
            >
              Download
            </a>
            <a
              href="/og-image.png"
              target="_blank"
              rel="noreferrer"
              className="inline-flex items-center gap-2 rounded-full border border-[#D96C5B] text-[#F7F6F2] px-4 py-2 text-sm font-semibold hover:bg-[#D96C5B]/10"
              data-testid="brand-og-open"
            >
              Social share card (1200×630)
            </a>
          </div>
        </div>
      </div>
    </div>
  );
}

// ---------- Bumblebee SVGs ----------
// Both drawn from primitive shapes so they stay crisp at any zoom.

function BeeWarm({ size = 100 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <defs>
        <clipPath id="beeA-body">
          <ellipse cx="50" cy="58" rx="26" ry="28" />
        </clipPath>
      </defs>
      {/* Wings behind body */}
      <ellipse cx="34" cy="38" rx="14" ry="9" fill="#F7F6F2" opacity="0.55" />
      <ellipse cx="66" cy="38" rx="14" ry="9" fill="#F7F6F2" opacity="0.55" />
      {/* Body base */}
      <ellipse cx="50" cy="58" rx="26" ry="28" fill="#F5C543" />
      {/* Stripes (clipped to body) */}
      <g clipPath="url(#beeA-body)">
        <rect x="20" y="48" width="60" height="6" fill="#1E3A2F" />
        <rect x="20" y="64" width="60" height="6" fill="#1E3A2F" />
      </g>
      {/* Antennae */}
      <path d="M43 33 Q39 24 44 18" stroke="#1E3A2F" strokeWidth="2" strokeLinecap="round" fill="none" />
      <path d="M57 33 Q61 24 56 18" stroke="#1E3A2F" strokeWidth="2" strokeLinecap="round" fill="none" />
      <circle cx="44" cy="18" r="1.8" fill="#1E3A2F" />
      <circle cx="56" cy="18" r="1.8" fill="#1E3A2F" />
      {/* Eyes */}
      <circle cx="43" cy="42" r="1.8" fill="#F7F6F2" />
      <circle cx="57" cy="42" r="1.8" fill="#F7F6F2" />
      {/* Smile */}
      <path d="M45 48 Q50 51 55 48" stroke="#1E3A2F" strokeWidth="1.4" strokeLinecap="round" fill="none" />
    </svg>
  );
}

function BeeHybrid({ size = 100 }) {
  // Option C: mix of A + B. Friendly round honey body from A, coral buzz-lines
  // and cleaner tech-forward face from B.
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      <defs>
        <clipPath id="beeC-body">
          <ellipse cx="52" cy="56" rx="24" ry="26" />
        </clipPath>
      </defs>
      {/* Buzz lines (from Option B) */}
      <line x1="8"  y1="46" x2="20" y2="46" stroke="#D96C5B" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="10" y1="56" x2="18" y2="56" stroke="#D96C5B" strokeWidth="2.5" strokeLinecap="round" />
      <line x1="8"  y1="66" x2="20" y2="66" stroke="#D96C5B" strokeWidth="2.5" strokeLinecap="round" />
      {/* Wings — softer than B, sharper than A */}
      <g className="wing-flap">
        <ellipse cx="38" cy="32" rx="12" ry="7" fill="#F7F6F2" opacity="0.75" />
        <ellipse cx="66" cy="32" rx="12" ry="7" fill="#F7F6F2" opacity="0.75" />
      </g>
      {/* Round friendly body (from A) — honey gold */}
      <ellipse cx="52" cy="56" rx="24" ry="26" fill="#F5C543" />
      {/* Two clean stripes clipped to body — thinner than A */}
      <g clipPath="url(#beeC-body)">
        <rect x="20" y="48" width="60" height="5" fill="#1E3A2F" />
        <rect x="20" y="64" width="60" height="5" fill="#1E3A2F" />
      </g>
      {/* Coral accent stinger */}
      <circle cx="74" cy="70" r="3.5" fill="#D96C5B" />
      {/* Sleek antennae */}
      <path d="M45 32 Q41 22 47 16" stroke="#1E3A2F" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      <path d="M59 32 Q63 22 57 16" stroke="#1E3A2F" strokeWidth="2.2" strokeLinecap="round" fill="none" />
      {/* Eyes only — no smile (tech-forward face from B) */}
      <circle cx="45" cy="44" r="2.2" fill="#1E3A2F" />
      <circle cx="59" cy="44" r="2.2" fill="#1E3A2F" />
    </svg>
  );
}


function BeeSignal({ size = 100 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 100 100" fill="none">
      {/* Motion / buzz lines behind body */}
      <line x1="6"  y1="44" x2="22" y2="44" stroke="#D96C5B" strokeWidth="3" strokeLinecap="round" />
      <line x1="10" y1="54" x2="20" y2="54" stroke="#D96C5B" strokeWidth="3" strokeLinecap="round" />
      <line x1="8"  y1="64" x2="24" y2="64" stroke="#D96C5B" strokeWidth="3" strokeLinecap="round" />

      {/* Wings — sharp, swept back */}
      <path d="M50 30 Q40 12 30 22 Q35 35 48 38 Z" fill="#F7F6F2" opacity="0.85" />
      <path d="M50 30 Q60 12 70 22 Q65 35 52 38 Z" fill="#F7F6F2" opacity="0.85" />

      {/* Body — bold teardrop pointing right */}
      <path d="M28 54 Q35 30 68 52 Q40 78 28 54 Z" fill="#F7F6F2" />

      {/* Single coral accent stripe */}
      <path d="M40 42 Q46 46 50 62" stroke="#D96C5B" strokeWidth="5" strokeLinecap="round" fill="none" />

      {/* Stinger / signal point */}
      <circle cx="70" cy="52" r="3.5" fill="#D96C5B" />

      {/* Eye */}
      <circle cx="36" cy="45" r="2" fill="#1E3A2F" />
    </svg>
  );
}
